import React from "react";
import { Activity, Briefcase, FileText, Globe, Users, Building2 } from "lucide-react";

import { cn } from "../../lib/utils";
import { Skeleton } from "../ui/skeleton";

const iconByFamily = {
  vc_portfolio: Building2,
  company_website: Globe,
  team_page: Users,
  careers_page: Briefcase,
  public_deck: FileText,
  signal_trail: Activity,
};

const getToneClasses = (percent) => {
  if (percent >= 75) {
    return {
      text: "text-emerald-300",
      bar: "bg-emerald-400",
      badge: "border-emerald-400/20 bg-emerald-400/10 text-emerald-300",
    };
  }

  if (percent >= 40) {
    return {
      text: "text-amber-300",
      bar: "bg-amber-300",
      badge: "border-amber-400/20 bg-amber-400/10 text-amber-300",
    };
  }

  return {
    text: "text-zinc-300",
    bar: "bg-zinc-400",
    badge: "border-zinc-700 bg-zinc-800/80 text-zinc-300",
  };
};

const ProgressRow = ({ family, sampledCount }) => {
  const Icon = iconByFamily[family.key] || Activity;
  const tone = getToneClasses(family.coveragePercent);

  return (
    <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-white/5 bg-white/5">
            <Icon className="h-4 w-4 text-zinc-200" />
          </div>
          <div className="min-w-0">
            <p className="font-['Outfit'] text-zinc-50">{family.label}</p>
            <p className="mt-1 text-sm font-['Manrope'] text-zinc-500">{family.description}</p>
          </div>
        </div>
        <div className="flex-shrink-0 text-right">
          <p className={cn("font-mono text-lg", tone.text)}>{family.coveredCount.toLocaleString()}</p>
          <p className="text-xs font-mono text-zinc-500">
            of {sampledCount.toLocaleString()}
          </p>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <div className="h-2 flex-1 rounded-full bg-zinc-800">
          <div
            className={cn("h-2 rounded-full transition-all duration-300", tone.bar)}
            style={{ width: family.coveragePercent > 0 ? `${Math.max(family.coveragePercent, 3)}%` : "0%" }}
          />
        </div>
        <div className={cn("rounded-full border px-2.5 py-1 text-[11px] font-mono uppercase tracking-[0.18em]", tone.badge)}>
          {family.coveragePercent}%
        </div>
      </div>
    </div>
  );
};

const LoadingRows = () => (
  <div className="space-y-3">
    {Array.from({ length: 4 }).map((_, index) => (
      <div key={index} className="rounded-2xl border border-white/5 bg-white/[0.02] p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Skeleton className="h-10 w-10 rounded-xl bg-white/10" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-28 bg-white/10" />
              <Skeleton className="h-3 w-56 bg-white/10" />
            </div>
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-12 bg-white/10" />
            <Skeleton className="h-3 w-16 bg-white/10" />
          </div>
        </div>
        <Skeleton className="mt-4 h-2 w-full rounded-full bg-white/10" />
      </div>
    ))}
  </div>
);

const EmptyState = () => (
  <div className="rounded-2xl border border-dashed border-zinc-800 bg-zinc-950/40 px-4 py-10 text-center">
    <p className="font-['Outfit'] text-zinc-50">No source coverage yet</p>
    <p className="mt-2 text-sm font-['Manrope'] text-zinc-500">
      This panel will populate once startup records are available for Week 2 source surfaces.
    </p>
  </div>
);

const SourceCoveragePanel = ({ coverage, loading }) => {
  const sampledCount = coverage?.sampledCount || 0;
  const families = coverage?.families || [];

  return (
    <section className="rounded-3xl border border-zinc-800 bg-zinc-900/70 p-5 shadow-[0_1px_0_rgba(255,255,255,0.02)]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Source Coverage</p>
          <h2 className="mt-2 font-['Outfit'] text-xl text-zinc-50">Coverage by source family</h2>
        </div>
        <div className="rounded-full border border-zinc-800 bg-zinc-950/70 px-3 py-1 text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-400">
          {coverage?.isSampled ? `Latest ${sampledCount.toLocaleString()}` : `${sampledCount.toLocaleString()} records`}
        </div>
      </div>

      <p className="mt-3 text-sm font-['Manrope'] text-zinc-500">
        Tracks which canonical startup records already expose each operator-facing sourcing surface.
      </p>

      <div className="mt-5">
        {loading ? (
          <LoadingRows />
        ) : families.length > 0 ? (
          <div className="space-y-3">
            {families.map((family) => (
              <ProgressRow key={family.key} family={family} sampledCount={sampledCount} />
            ))}
          </div>
        ) : (
          <EmptyState />
        )}
      </div>
    </section>
  );
};

export default SourceCoveragePanel;
